import { MEVETH_CONTRACT, MULTISIG_CONTRACT } from '@/constants/addresses';
import 'react-tooltip/dist/react-tooltip.css';
import { captureException } from '@sentry/nextjs';
import { useEffect, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { formatEther } from 'viem';
import { useBalance, useContractRead } from 'wagmi';
import aggregatorV3InterfaceABI from '../../../aggregatorV3InterfaceABI';
import MevEthAbi from '../../../MevEthAbi';
import { StatCell, StatsContainer } from '../general/StatCells';

function StakeStats() {
  const [totalStaked, setTotalStaked] = useState<string>('0');
  const [fraction, setFraction] = useState<BigInt[]>([0n, 0n]);
  const [currentAPR, setCurrentAPR] = useState('0');
  const [currentAPY, setCurrentAPY] = useState('0');
  const [totalUsdStaked, setTotalUsdStaked] = useState<string>('0');
  const [ethPrice, setETHPrice] = useState<number>(0);
  const { data: totalAssets, error } = useContractRead({
    address: MEVETH_CONTRACT,
    abi: MevEthAbi,
    functionName: 'totalAssets',
  });
  interface RewardsSummary {
    summary: {
      totalRewards: number;
      withdrawals: number;
      proposedBlocks: number;
      proposedBlocksRewards: number;
      withdrawalsAmount: number;
      latestBlock: number;
      latestBlockTime: string;
    };
  }
  const {
    data: ETHUSDPrice,
    isError,
    isLoading,
  } = useContractRead({
    address: '0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419',
    abi: aggregatorV3InterfaceABI,
    functionName: 'latestRoundData',
  });

  const { data: fractionResult } = useContractRead({
    address: MEVETH_CONTRACT,
    abi: MevEthAbi,
    functionName: 'fraction',
  });
  useEffect(() => {
    if (ETHUSDPrice) {
      const priceArray = ETHUSDPrice as [BigInt, BigInt];
      const price = priceArray[1];
      if (price) {
        setETHPrice(parseFloat(price.toString()) / 10 ** 8);
      }
    }
  }, [ETHUSDPrice]);
  useEffect(() => {
    if (fractionResult) {
      setFraction(fractionResult as BigInt[]);
    }
  }, [fractionResult]);

  const [rewardsSummary, setRewardsSummary] = useState<RewardsSummary | null>(null);
  const computeAPR = () => {
    /*
    Sandy Bradley:
    from datetime import datetime
    import time

    elastic = 12287964464673847851480
    base = 12193676060786263808760

    year_in_sec = 31536000

    # calc time since launch
    start_time = "08/15/2023"
    end_time = datetime.now()
    # convert time string to datetime
    t1 = datetime.strptime(start_time, "%m/%d/%Y")
    t2 = end_time

    # get difference
    delta = t2 - t1

    # time difference in seconds
    time_since_launch = delta.total_seconds()

    print('Start time:', t1)
    print('End time:', end_time)
    print(f"Time difference is {time_since_launch} seconds")

    apy_base = 100 * (elastic - base) * year_in_sec / (time_since_launch * base)
    print(f"APY base:", apy_base)

    I guess a more conservative compound would be once per quarter:
    apy_comp = 100 * ((1 + apy_base/100) ** 4 - 1)
    print(f"APY comp:", apy_comp)
    APY comp: 8.866209571922191
   */

    const startTime = new Date(1696426103 * 1000);
    const endTime = new Date();
    const elastic = fraction[0] as BigInt;
    const base = fraction[1] as BigInt;
    const yearInSeconds = 31536000;
    const timeSinceLaunch = (endTime.getTime() - startTime.getTime()) / 1000;

    console.log(' Start time:', startTime.toISOString());
    console.log(' End time:', endTime.toISOString());
    console.log(` Time difference is ${timeSinceLaunch} seconds`);

    const apyBase =
      (100 * (parseFloat(elastic.toString()) - parseFloat(base.toString())) * yearInSeconds) /
      (timeSinceLaunch * parseFloat(base.toString()));
    console.log(` APY base:`, apyBase);
    const apyComp = 100 * ((1 + apyBase / 100) ** 2 - 1);

    console.log(` APY compounding:`, apyComp);

    setCurrentAPY(apyBase.toFixed(2));
    setCurrentAPR(apyComp.toFixed(2));
  };

  useEffect(() => {
    console.log('APY compounding rewards', rewardsSummary?.summary.latestBlock);
    if (fractionResult && fraction[0]) computeAPR();
  }, [rewardsSummary?.summary.latestBlock, fraction, fractionResult]);

  useEffect(() => {
    try {
      if (totalAssets) {
        const totalStakedData: string = formatEther(totalAssets as unknown as bigint);
        if (totalAssets) {
          setTotalStaked(totalStakedData);
          const total = (parseFloat(totalStakedData) * ethPrice) / 10; // 10 ** 18;
          setTotalUsdStaked(total.toFixed(0));
        }
      }
    } catch (error) {
      captureException(new Error('Failed to format totalAssets'), {});
    }
  }, [ethPrice, totalAssets]);

  const didLoad = ethPrice !== 0;
  return (
    <StatsContainer>
      <StatCell
        // Logo={MEVETHLogo}
        didLoad={didLoad}
        title="Total Value Locked"
        subtitle={`${new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          signDisplay: 'never',
        })
          .format(parseFloat(totalStaked))
          .replace('$', '')} Ξ`}
      />
      <StatCell
        didLoad={didLoad}
        title="Compound APY"
        alt={`Base APY: ${currentAPY}%`}
        subtitle={`${currentAPR}%`}
      />
      <StatCell
        didLoad={didLoad}
        title="Staked"
        subtitle={`$${(parseFloat(totalUsdStaked) / 100000).toFixed(2)}m`}
      />
    </StatsContainer>
  );
}

export default StakeStats;
